import { useCallback, useEffect, useRef, type Dispatch } from "react";
import {
  CompletedSteps,
  ContactDetailsSubStep,
  ProvetDialogStep,
  getContactDetailsSubStep
} from "../types";
import {
  ProvetBookingFlowAction,
  ProvetBookingFlowActionType,
  ProvetBookingFlowState
} from "./provetBookingFlowReducer";
import { TrackingState } from "./useProvetTracking";

interface UseBookingStepsOptions {
  bookingFlowState: ProvetBookingFlowState;
  bookingFlowDispatch: Dispatch<ProvetBookingFlowAction>;
  tracking: TrackingState;
}

interface BookingStepsHandlers {
  updateBookingStep: (
    provetDialogStep: ProvetDialogStep,
    isComplete?: boolean
  ) => Promise<void>;
  setEditDialog: (
    step: ProvetDialogStep,
    isComplete?: boolean,
    transition?: boolean
  ) => Promise<void>;
  /**
   * Tracks moving to another sub-step of the ContactDetails step (the previous sub-step is
   * tracked as completed).
   */
  changeContactDetailsSubStep: (subStep: ContactDetailsSubStep) => Promise<void>;
  /**
   * Forgets the phone verification and returns to the ContactDetails step at the email and
   * phone input, eg when the verification has expired by the time of booking (403).
   */
  restartPhoneVerification: () => Promise<void>;
}

/**
 * Hook for managing booking flow step navigation.
 * Handles step transitions, completion tracking, and important information acknowledgment.
 */
export const useBookingSteps = ({
  bookingFlowState,
  bookingFlowDispatch,
  tracking
}: UseBookingStepsOptions): BookingStepsHandlers => {
  // Access the latest completed steps from callbacks that may run after a delay, when their
  // closure would otherwise hold outdated state (eg right after the phone has been verified)
  const completedStepsRef = useRef(bookingFlowState.completedSteps);

  useEffect(() => {
    completedStepsRef.current = bookingFlowState.completedSteps;
  }, [bookingFlowState.completedSteps]);

  // The sub-steps of ContactDetails are tracked as separate steps. This is the sub-step that
  // the user is currently on, for the events of the ContactDetails step.
  const contactDetailsSubStepRef = useRef<ContactDetailsSubStep>(
    getContactDetailsSubStep(bookingFlowState.completedSteps)
  );

  const changeContactDetailsSubStep = useCallback(
    async (subStep: ContactDetailsSubStep) => {
      const previousSubStep = contactDetailsSubStepRef.current;

      if (subStep === previousSubStep) {
        return;
      }

      contactDetailsSubStepRef.current = subStep;

      await tracking.trackingService.trackBookingEvent(
        ProvetDialogStep.ContactDetails,
        true,
        previousSubStep
      );
      await tracking.trackingService.trackBookingEvent(
        ProvetDialogStep.ContactDetails,
        false,
        subStep
      );
    },
    [tracking.trackingService]
  );

  const updateBookingStep = useCallback(
    async (provetDialogStep: ProvetDialogStep, isComplete: boolean = true) => {
      if (provetDialogStep !== bookingFlowState.activeStep) {
        // Only track leaving a step if it was completed. Leaving it incomplete (eg going back
        // from the summary modal) would repeat the event that was emitted when it was entered.
        if (isComplete) {
          await tracking.trackingService.trackBookingEvent(
            bookingFlowState.activeStep,
            true,
            contactDetailsSubStepRef.current
          );
        }

        bookingFlowDispatch({
          type: ProvetBookingFlowActionType.SetActiveStep,
          step: provetDialogStep
        });

        if (provetDialogStep === ProvetDialogStep.ContactDetails) {
          // The phone verification keeps its progress while the user is on another step, so
          // keep the sub-step that the user left off at (eg when returning to the SMS code
          // input after changing the date) unless the phone has been verified in the meantime.
          const derivedSubStep = getContactDetailsSubStep(completedStepsRef.current);
          const lastSubStep = contactDetailsSubStepRef.current;

          if (derivedSubStep !== "details" && lastSubStep !== "details") {
            contactDetailsSubStepRef.current = lastSubStep;
          } else {
            contactDetailsSubStepRef.current = derivedSubStep;
          }
        }
      } else if (provetDialogStep === ProvetDialogStep.ContactDetails) {
        // Moving between the sub-steps of ContactDetails (without changing step) is tracked
        // by changeContactDetailsSubStep
        return;
      }

      await tracking.trackingService.trackBookingEvent(
        provetDialogStep,
        false,
        contactDetailsSubStepRef.current
      );
    },
    [bookingFlowState.activeStep, bookingFlowDispatch, tracking.trackingService]
  );

  const setEditDialog = useCallback(
    async (
      step: ProvetDialogStep,
      isComplete: boolean = false,
      transition: boolean = true
    ) => {
      // Only dispatch the specific fields we want to update
      // The reducer will merge these with existing completedSteps
      const completedStepsUpdate: Partial<CompletedSteps> = {};

      switch (step) {
        case ProvetDialogStep.SelectClinic:
          // Changing the clinic clears the treatment and time, so the later steps aren't
          // complete any more either (not just once a new clinic has been picked)
          completedStepsUpdate.selectClinic = false;
          completedStepsUpdate.selectTreatment = false;
          completedStepsUpdate.selectDateAndTime = false;
          completedStepsUpdate.selectContactDetails = false;
          break;
        case ProvetDialogStep.SelectTreatment:
          completedStepsUpdate.selectClinic = true;
          completedStepsUpdate.selectTreatment = false;
          break;
        case ProvetDialogStep.SelectDateAndTime:
          completedStepsUpdate.selectTreatment = true;
          completedStepsUpdate.selectDateAndTime = false;
          break;
        case ProvetDialogStep.ContactDetails:
          completedStepsUpdate.selectDateAndTime = true;
          completedStepsUpdate.selectContactDetails = false;
          break;
        default:
          // No change for other steps
          break;
      }

      bookingFlowDispatch({
        type: ProvetBookingFlowActionType.SetCompletedSteps,
        completedSteps: completedStepsUpdate
      });

      if (transition) {
        setTimeout(async () => {
          await updateBookingStep(step, isComplete);
        }, 200);
      } else {
        await updateBookingStep(step, isComplete);
      }
    },
    [bookingFlowDispatch, updateBookingStep]
  );

  const restartPhoneVerification = useCallback(async () => {
    const completedStepsUpdate: Partial<CompletedSteps> = {
      verifyPhone: false,
      selectContactDetails: false
    };

    bookingFlowDispatch({
      type: ProvetBookingFlowActionType.SetFormData,
      formData: {
        phoneVerified: false,
        verificationRequestId: undefined
      }
    });
    bookingFlowDispatch({
      type: ProvetBookingFlowActionType.SetCompletedSteps,
      completedSteps: completedStepsUpdate
    });

    // The ref is otherwise only updated after the next render. Updating it now makes
    // updateBookingStep open (and track) the step at the email and phone input, not at the
    // details that the phone verification used to lead to.
    completedStepsRef.current = { ...completedStepsRef.current, ...completedStepsUpdate };

    await updateBookingStep(ProvetDialogStep.ContactDetails, false);
  }, [bookingFlowDispatch, updateBookingStep]);

  return {
    updateBookingStep,
    setEditDialog,
    changeContactDetailsSubStep,
    restartPhoneVerification
  };
};
